import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2, Check, Maximize2, Clapperboard } from 'lucide-react';
import Shell from '../../components/Shell';
import Seo from '../../components/Seo';
import { Section } from '../../components/Pieces';
import Modal from '../../components/Modal';
import { projectVideos } from '../../data/projectsData';

export default function ProjectVideos() {
  const [copied, setCopied] = useState(null);
  const [playing, setPlaying] = useState(null);

  const copyLink = async (video) => {
    const shareUrl = `${window.location.origin}${video.src}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (e) {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(video.number);
    setTimeout(() => setCopied((c) => (c === video.number ? null : c)), 2000);
  };

  return (
    <Shell>
      <Seo title="Project Videos"
        description="Field footage, worker testimonials, classroom sessions and impact documentation from Mashmari's Digital Anganwadi projects in Ambala and Satnavari."
        image="/video-thumbs/video-04.jpg" />

      <section className="relative overflow-hidden bg-[var(--mm-cream)] border-b border-[var(--mm-line)]">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] mm-dot-halo-light rounded-full opacity-60" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20 relative">
          <BackToProjects />
          <div className="mt-8">
            <span className="inline-flex w-14 h-14 rounded-2xl bg-[#F3E8FA] text-[var(--mm-purple)] items-center justify-center">
              <Clapperboard className="w-6 h-6" />
            </span>
          </div>
          <h1 className="mt-6 font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] leading-[1.08]">Project Videos</h1>
          <p className="mt-6 text-base md:text-lg text-[var(--mm-ink)]/70 max-w-3xl leading-relaxed">
            Field footage, worker testimonials, classroom sessions and impact documentation from Mashmari's Digital Anganwadi projects.
            Use the share icon on any video to copy its link for PowerPoint or reference.
          </p>
        </div>
      </section>

      <Section bg="white">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
          {projectVideos.map((v) => (
            <article key={v.number} className="bg-white border border-[var(--mm-line)] rounded-2xl overflow-hidden flex flex-col transition-all hover:shadow-[0_20px_44px_-26px_rgba(74,18,89,.4)]"
              data-testid={`video-card-${v.number.replace(' ', '-').toLowerCase()}`}>
              <div className="relative bg-[var(--mm-purple-dark)]">
                <span className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.14em] uppercase font-semibold bg-white/90 text-[var(--mm-purple)] rounded-full px-3 py-1">▶ {v.badge}</span>
                <button onClick={() => setPlaying(v)} aria-label={`Open ${v.title} in full view`} data-testid={`video-expand-${v.number.replace(' ', '-').toLowerCase()}`}
                  className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 text-[var(--mm-purple)] flex items-center justify-center hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                  <Maximize2 className="w-3.5 h-3.5" />
                </button>
                <video controls preload="none" poster={v.poster} className="w-full aspect-video bg-black object-cover" data-testid={`video-player-${v.number.replace(/\s+/g, '-').toLowerCase()}`}>
                  <source src={v.src} type="video/mp4" />
                  Your browser does not support embedded video.
                </video>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="mm-kicker">{v.number}</div>
                <div className="mt-2 flex items-start justify-between gap-3">
                  <h4 className="font-display text-lg font-bold text-[var(--mm-ink)] leading-snug">{v.title}</h4>
                  <button onClick={() => copyLink(v)} title="Copy video link" aria-label={`Copy link to ${v.title}`}
                    data-testid={`video-share-${v.number.replace(' ', '-').toLowerCase()}`}
                    className="shrink-0 w-9 h-9 rounded-full border border-[var(--mm-line)] flex items-center justify-center text-[var(--mm-purple)] hover:border-[var(--mm-purple)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
                    {copied === v.number ? <Check className="w-4 h-4 text-green-600" /> : <Share2 className="w-4 h-4" />}
                  </button>
                </div>
                {copied === v.number && <p className="mt-2 text-xs font-semibold text-green-700" data-testid="copied-toast">Link copied!</p>}
                <p className="mt-3 text-sm text-[var(--mm-ink)]/70 leading-relaxed flex-1">{v.desc}</p>
                <div className="mt-5 pt-4 border-t border-[var(--mm-line)]">
                  <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--mm-purple)] bg-[#F3E8FA] rounded-full px-3 py-1">{v.tag}</span>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14"><BackToProjects /></div>
      </Section>

      {playing && (
        <Modal wide title={`${playing.number} · ${playing.title}`} onClose={() => setPlaying(null)} testid="video-modal">
          <video controls autoPlay poster={playing.poster} className="w-full max-h-[70vh] bg-black" data-testid="video-modal-player">
            <source src={playing.src} type="video/mp4" />
          </video>
          <div className="px-5 md:px-7 py-5 space-y-4">
            <p className="text-sm text-[var(--mm-ink)]/70 leading-relaxed">{playing.desc}</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => copyLink(playing)} className="btn-primary text-xs" data-testid="video-modal-share">
                {copied === playing.number ? 'Link copied!' : 'Copy link'} <Share2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setPlaying(null)} className="btn-outline text-xs" data-testid="video-modal-back">
                Back to gallery
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Shell>
  );
}

function BackToProjects() {
  return (
    <Link to="/projects" data-testid="back-to-projects"
      className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--mm-purple)] hover:gap-3 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)] rounded-full">
      <ArrowLeft className="w-4 h-4" /> Back to Projects
    </Link>
  );
}
